import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send } from "lucide-react";
import { api, getToken } from "../lib/api";
import { useLang } from "../context/LangContext";

export default function AiAssistant() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [thinking, setThinking] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking, open]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = text.trim();
    if (!msg || thinking) return;
    const next = [...messages, { role: "user", content: msg }];
    setMessages(next);
    setText("");
    setThinking(true);
    try {
      const res = await api("/support/ai", {
        method: "POST",
        auth: Boolean(getToken()),
        body: { message: msg, history: next.slice(-10, -1) },
      });
      setMessages([...next, { role: "assistant", content: res.answer || res.message || "…" }]);
    } catch {
      setMessages([...next, { role: "assistant", content: t("ai_error") }]);
    } finally {
      setThinking(false);
    }
  };

  return (
    <>
      {/* Floating AI button */}
      <button
        data-testid="ai-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label="Marhine AI"
        className="fixed bottom-36 right-4 z-50 flex h-13 w-13 items-center justify-center rounded-full bg-teal-700 p-3.5 text-white shadow-xl shadow-teal-700/30 transition hover:bg-teal-800 active:scale-90 md:bottom-8 md:right-8"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>

      {open && (
        <div
          data-testid="ai-panel"
          className="fixed bottom-48 right-4 z-50 flex h-[26rem] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl md:bottom-24 md:right-8"
        >
          <div className="flex items-center gap-2 bg-teal-950 px-4 py-3.5 text-white">
            <Sparkles className="h-5 w-5 text-amber-400" />
            <div>
              <p className="font-heading text-sm font-extrabold">{t("ai_name")}</p>
              <p className="text-[10px] text-teal-200/80">{t("ai_sub")}</p>
            </div>
          </div>

          <div data-testid="ai-messages" className="flex-1 space-y-2.5 overflow-y-auto bg-slate-50 p-3.5">
            <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white px-3.5 py-2.5 text-sm text-slate-800 shadow-sm">
              {t("ai_greeting")}
            </div>
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`ai-message-${i}`}
                className={`max-w-[85%] px-3.5 py-2.5 text-sm shadow-sm ${
                  m.role === "user"
                    ? "ml-auto rounded-2xl rounded-tr-sm bg-teal-700 text-white"
                    : "rounded-2xl rounded-tl-sm bg-white text-slate-800"
                }`}
              >
                {m.content}
              </div>
            ))}
            {thinking && (
              <div data-testid="ai-thinking" className="inline-flex gap-1 rounded-2xl rounded-tl-sm bg-white px-4 py-3 shadow-sm">
                <span className="h-2 w-2 animate-bounce rounded-full bg-teal-400 [animation-delay:0ms]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-teal-400 [animation-delay:150ms]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-teal-400 [animation-delay:300ms]" />
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="flex items-center gap-2 border-t border-slate-100 bg-white p-2.5">
            <input
              data-testid="ai-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("ai_placeholder")}
              maxLength={1000}
              className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-teal-500"
            />
            <button
              data-testid="ai-send"
              type="submit"
              disabled={thinking || !text.trim()}
              aria-label="Send"
              className="shrink-0 rounded-full bg-teal-700 p-2.5 text-white transition hover:bg-teal-800 active:scale-90 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
